import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from 'react';
import { Chip, CurrencyFormattedDisplay, MenuItem, SearchField, ServerDataGrid, Typography, useTheme, Select, Spacer, Grid } from '@emburse/embark-core';
import { IconMenu } from '../../../components';
import { AllowanceFilter, AllowanceFilterLabel } from '../../../interfaces/Allowance';
import { formatDate } from '../../../util/dateAdapter';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import SkeletonDataGrid from '../../../components/SkeletonDataGrid';
function CellText({ active, children }) {
    const theme = useTheme();
    return (_jsx(Typography, { style: { color: active ? 'initial' : theme.palette.text.disabled }, variant: "body2", noWrap: true, children: children }));
}
function CorpAllowancesTable({ loading, selectedRowHandler, onSearchChange, onFilterChange, onSortChange, onPageChange, onDelete, onEdit, rowCount, rows, selectedPageSize, selectedPage }) {
    useTranslation();
    const onUpdated = useCallback((page, pageSize, sortModel, details) => {
        if (sortModel) {
            onSortChange(sortModel);
        }
        else {
            onPageChange(page, details, pageSize);
        }
    }, [onSortChange, onPageChange]);
    const renderExpCats = (expCats) => {
        const charLength = 30;
        let displayExpCats = '';
        const remainExpCats = [];
        expCats.forEach(({ Name }) => {
            if (Name.length <= charLength - displayExpCats.length) {
                displayExpCats += `${Name}, `;
            }
            else {
                remainExpCats.push(Name);
            }
        });
        displayExpCats = displayExpCats.trim().slice(0, -1);
        return remainExpCats.length ? `${displayExpCats} +${remainExpCats.length}` : displayExpCats;
    };
    const columns = [
        {
            field: 'Allowance_Inactive',
            headerName: $t('Status'),
            width: 90,
            sortable: !loading,
            renderCell: (cell) => (_jsx(Chip, { size: "mini", variant: "outlined", label: cell.row.Active ? $t('Active') : $t('Inactive'), color: cell.row.Active ? 'success' : 'monochrome' }))
        },
        {
            field: 'Allowance_EmpFK',
            headerName: $t('User'),
            width: 220,
            sortable: !loading,
            renderCell: (cell) => (_jsx(CellText, { active: cell.row.Active, children: cell.row.Employee.FullName }))
        },
        {
            field: 'Allowance_Name',
            headerName: $t('Allowance Name'),
            width: 137,
            sortable: !loading,
            renderCell: (cell) => _jsx(CellText, { active: cell.row.Active, children: cell.row.Name })
        },
        {
            field: 'Allowance_Code',
            headerName: $t('Code'),
            width: 72,
            sortable: !loading,
            renderCell: (cell) => _jsx(CellText, { active: cell.row.Active, children: cell.row.Code })
        },
        {
            field: 'Allowance_StartDate',
            headerName: $t('Start Date'),
            width: 115,
            sortable: !loading,
            renderCell: (cell) => (_jsx(CellText, { active: cell.row.Active, children: formatDate(cell.row.StartDate) }))
        },
        {
            field: 'Allowance_EndDate',
            headerName: $t('End Date'),
            width: 115,
            sortable: !loading,
            renderCell: (cell) => (_jsx(CellText, { active: cell.row.Active, children: formatDate(cell.row.EndDate) }))
        },
        {
            field: 'Allowance_Amount',
            headerName: $t('Amount'),
            width: 135,
            type: 'number',
            sortable: !loading,
            renderCell: (cell) => (_jsx(CellText, { active: cell.row.Active, children: _jsx(CurrencyFormattedDisplay, { amount: cell.row.Amount.Value, code: cell.row.CurrencyType.Suffix }) }))
        },
        {
            field: 'Allowance_RequiredForSubmission',
            headerName: $t('Stop'),
            width: 60,
            sortable: !loading,
            renderCell: (cell) => (_jsx(CellText, { active: cell.row.Active, children: cell.row.RequiredForSubmission ? 'Yes' : 'No' }))
        },
        {
            field: 'ExpCats',
            headerName: $t('Categories'),
            width: 260,
            sortable: false,
            renderCell: (cell) => (_jsx(CellText, { active: cell.row.Active, children: renderExpCats(cell.row.ExpCats) }))
        },
        {
            field: 'ActionPlaceholder',
            renderHeader: () => _jsx("span", {}),
            width: 50,
            renderCell: (cell) => (_jsx("div", { style: {
                    width: '100%',
                    textAlign: 'center'
                }, children: _jsxs(IconMenu, { disabled: loading, anchorHorizontalPosition: "left", onOpen: () => {
                        selectedRowHandler({ ...cell.row });
                    }, children: [_jsx(MenuItem, { onClick: () => {
                                onEdit();
                            }, style: { width: '96px ' }, children: "Edit" }), _jsx(MenuItem, { onClick: () => {
                                onDelete();
                            }, style: { width: '96px ' }, children: "Delete" })] }) }))
        }
    ];
    const searchChangeHandler = useCallback((value) => {
        onSearchChange(value);
    }, [onSearchChange]);
    const filterOptions = () => [...AllowanceFilterLabel].map((x) => (_jsx(MenuItem, { value: x[0], children: x[1] }, x[0])));
    const filterChangeHandler = (e) => {
        onFilterChange(e.target.value);
    };
    const getDefaultFilter = () => {
        const cachedPref = localStorage.getItem('afls');
        return Object.keys(AllowanceFilter).includes(cachedPref)
            ? parseInt(cachedPref, 10)
            : AllowanceFilter.InProgress;
    };
    return (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 32 }), _jsxs(Grid, { container: true, spacing: 3, direction: "row", justifyContent: "flex-start", alignItems: "flex-start", children: [_jsx(Grid, { item: true, xs: 6, sm: 2, style: { minWidth: '200px' }, children: _jsx(SearchField, { label: $t('Search'), id: "search", disabled: loading, doSearchOnChange: true, onChangeTimeout: 750, searchAction: searchChangeHandler }) }), _jsx(Grid, { item: true, xs: 6, sm: 3, children: _jsx(Select, { label: "", id: "filter", defaultValue: getDefaultFilter(), disabled: loading, onChange: filterChangeHandler, children: filterOptions() }) })] }), _jsx(Spacer, { size: 24 }), !loading ? (_jsx(ServerDataGrid, { rowCount: rowCount, autoHeight: true, loading: loading, onUpdated: onUpdated, rows: rows, pageSizeOptions: [25, 50, 100], initialState: {
                    pagination: {
                        paginationModel: {
                            page: selectedPage,
                            pageSize: selectedPageSize
                        }
                    }
                }, localeText: {
                    noRowsLabel: $t('There are no Allowances matching the current filters and/or search terms.')
                }, columns: columns })) : (_jsx(SkeletonDataGrid, { columns: columns.map((column) => ({
                    field: column.field,
                    headerName: column.field === 'ActionPlaceholder' ? '' : column.headerName,
                    width: column.width,
                    type: column.type
                })), rows: [], skeletonRows: selectedPageSize, sx: {
                    width: '100%',
                    '& .MuiDataGrid-virtualScroller': {
                        overflow: 'hidden'
                    }
                }, autoHeight: true, loading: true }))] }));
}
export default CorpAllowancesTable;
